import { Link, useNavigate } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';
import { AuthContext } from '@/App';
import { Menu, X, LogOut, Facebook, Instagram, Youtube } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DonationButton from '@/components/DonationButton';
import LoginModal from '@/components/LoginModal';
import BooksNavMenu from '@/components/BooksNavMenu';
import EditModeToggle from '@/components/EditModeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function FooterMailingList() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/mailing-list/subscribe`, null, {
        params: { email }
      });
      toast.success(response.data.message);
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="flex-1 px-3 py-2 text-sm rounded border-2 border-[#6B4423] bg-[#3D2817] text-[#F4E4C1] placeholder-[#D4A574] focus:border-[#E8934D] outline-none"
      />
      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 text-sm font-bold bg-[#E8934D] text-white rounded hover:bg-[#E85D4D] transition-colors disabled:opacity-50"
      >
        {loading ? '...' : 'Join'}
      </button>
    </form>
  );
}

export default function Layout({ children }) {
  const { user, logout, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [siteConfig, setSiteConfig] = useState({ site_name: 'AcuErnie', logo_url: null });
  const [socialLinks, setSocialLinks] = useState({ facebook: null, instagram: null, youtube: null });
  const [pageSettings, setPageSettings] = useState([]);

  useEffect(() => {
    loadSiteConfig();
    loadSocialLinks();
    loadPageSettings();
  }, []);

  const loadSiteConfig = async () => {
    try {
      const response = await axios.get(`${API}/site-config`);
      setSiteConfig(response.data);
    } catch (error) {
      console.error('Failed to load site config:', error);
    }
  };

  const loadSocialLinks = async () => {
    try {
      const response = await axios.get(`${API}/social-links`);
      setSocialLinks(response.data);
    } catch (error) {
      console.error('Failed to load social links:', error);
    }
  };

  const loadPageSettings = async () => {
    try {
      const response = await axios.get(`${API}/page-settings`);
      setPageSettings(response.data);
    } catch (error) {
      console.error('Failed to load page settings:', error);
    }
  };

  const handleLogin = () => {
    setShowLoginModal(true);
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setShowLoginModal(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen retro-gradient page-container">
      {/* Header - Compact Horizontal Layout */}
      <header 
        className="border-b-4 border-[#E85D4D] bg-gradient-to-r from-[#2C1810] via-[#E85D4D]/20 to-[#2C1810] shadow-lg"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            
            {/* Left: Logo (Doubled Size) */}
            <div className="flex-shrink-0" data-testid="logo-container">
              {siteConfig.logo_url && (
                <img 
                  src={`${BACKEND_URL}${siteConfig.logo_url}`} 
                  alt={siteConfig.site_name}
                  className="h-32 md:h-40 w-auto drop-shadow-lg"
                  data-testid="site-logo"
                />
              )}
            </div>

            {/* Center: Navigation Menu */}
            <nav className="hidden lg:flex items-center space-x-1 flex-1 justify-center px-4">
              {pageSettings
                .filter(page => page.is_visible && page.page_name !== 'books')
                .sort((a, b) => a.menu_order - b.menu_order)
                .map(page => (
                  <Link
                    key={page.page_name}
                    to={page.route}
                    className="nav-link text-sm"
                    data-testid={`nav-${page.page_name}`}
                  >
                    {page.display_name}
                  </Link>
                ))}
              <BooksNavMenu />
            </nav>

            {/* Right: Action Buttons */}
            <div className="flex items-center space-x-2">
              {user ? (
                <>
                  <Link 
                    to="/my-learning" 
                    className="hidden md:block px-4 py-2 text-sm font-bold bg-[#E8934D] text-white rounded hover:bg-[#E85D4D] transition-colors whitespace-nowrap"
                    data-testid="nav-my-learning"
                  >
                    MY LEARNING
                  </Link>
                  {user?.role === 'admin' && (
                    <Link 
                      to="/admin" 
                      className="hidden md:block px-4 py-2 text-sm font-bold bg-[#E85D4D] text-white rounded hover:bg-[#E8934D] transition-colors"
                      data-testid="nav-admin"
                    >
                      ADMIN
                    </Link>
                  )}
                  <button 
                    onClick={() => setSidebarOpen(true)} 
                    className="px-3 py-2 text-sm font-bold bg-[#6B4423] text-[#F4E4C1] rounded hover:bg-[#3D2817] transition-colors flex items-center gap-2 whitespace-nowrap"
                    data-testid="user-menu-button"
                  >
                    <Menu className="w-4 h-4" />
                    <span>{user.name?.split(' ')[0] || 'Menu'}</span>
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={handleLogin} 
                    className="hidden md:block px-4 py-2 text-sm font-bold bg-[#E8934D] text-white rounded hover:bg-[#E85D4D] transition-colors border-2 border-[#E85D4D]" 
                    data-testid="login-button"
                  >
                    Login
                  </button>
                  {/* Mobile Menu Toggle for non-logged in users */}
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="md:hidden p-2 rounded bg-[#E8934D] text-white hover:bg-[#E85D4D] transition-colors"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

          </div>
        </div>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Menu */}
      <aside
        className={`fixed top-0 left-0 h-full w-80 bg-gradient-to-b from-[#2C1810] to-[#3D2817] border-r-4 border-[#E85D4D] shadow-2xl z-50 transition-transform duration-300 overflow-y-auto ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6">
          {/* Close Button */}
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl retro-title text-[#F4E4C1]">Menu</h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg bg-[#E8934D] text-white hover:bg-[#E85D4D] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="space-y-2">
            {pageSettings
              .filter(page => page.is_visible && page.page_name !== 'books')
              .sort((a, b) => a.menu_order - b.menu_order)
              .map(page => (
                <Link
                  key={page.page_name}
                  to={page.route}
                  onClick={() => setSidebarOpen(false)}
                  className="block px-4 py-3 rounded-lg text-[#F4E4C1] hover:bg-[#E8934D] hover:text-white transition-all font-bold text-lg border-2 border-transparent hover:border-[#E85D4D]"
                  data-testid={`sidebar-${page.page_name}`}
                >
                  {page.display_name}
                </Link>
              ))}
            <Link
              to="/books"
              onClick={() => setSidebarOpen(false)}
              className="block px-4 py-3 rounded-lg text-[#F4E4C1] hover:bg-[#E8934D] hover:text-white transition-all font-bold text-lg border-2 border-transparent hover:border-[#E85D4D]"
              data-testid="sidebar-books"
            >
              Books
            </Link>
          </nav>

          {/* User Section */}
          <div className="mt-8 pt-6 border-t-2 border-[#6B4423]">
            {/* Study Materials Section */}
            <div className="mb-6">
              <div className="px-4 mb-3 text-[#D4A574] text-xs font-bold uppercase tracking-wider">
                Study Materials
              </div>
              <Link
                to="/flashcards-center"
                onClick={() => setSidebarOpen(false)}
                className="block px-4 py-3 rounded-lg text-[#F4E4C1] hover:bg-purple-600 hover:text-white transition-all font-semibold"
              >
                📚 Flashcards
              </Link>
              <Link
                to="/matching-center"
                onClick={() => setSidebarOpen(false)}
                className="block px-4 py-3 rounded-lg text-[#F4E4C1] hover:bg-blue-600 hover:text-white transition-all font-semibold"
              >
                🔀 Matching Games
              </Link>
              <Link
                to="/study-guide-center"
                onClick={() => setSidebarOpen(false)}
                className="block px-4 py-3 rounded-lg text-[#F4E4C1] hover:bg-green-600 hover:text-white transition-all font-semibold"
              >
                📖 Study Guides
              </Link>
              <Link
                to="/memory-game-center"
                onClick={() => setSidebarOpen(false)}
                className="block px-4 py-3 rounded-lg text-[#F4E4C1] hover:bg-pink-600 hover:text-white transition-all font-semibold"
              >
                🧠 Memory Game
              </Link>
              <Link
                to="/fill-in-center"
                onClick={() => setSidebarOpen(false)}
                className="block px-4 py-3 rounded-lg text-[#F4E4C1] hover:bg-blue-500 hover:text-white transition-all font-semibold"
              >
                ✏️ Fill In
              </Link>
              <Link
                to="/jeopardy-center"
                onClick={() => setSidebarOpen(false)}
                className="block px-4 py-3 rounded-lg text-[#F4E4C1] hover:bg-orange-600 hover:text-white transition-all font-semibold"
              >
                🏆 Jeopardy Game
              </Link>
            </div>
            
            {user ? (
              <div className="space-y-3">
                <Link
                  to="/my-learning"
                  onClick={() => setSidebarOpen(false)}
                  className="block px-4 py-3 rounded-lg text-[#F4E4C1] hover:bg-[#E8934D] hover:text-white transition-all font-bold border-2 border-transparent hover:border-[#E85D4D]"
                >
                  My Learning
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    onClick={() => setSidebarOpen(false)}
                    className="block px-4 py-3 rounded-lg text-[#2C1810] bg-[#E8934D] hover:bg-[#E85D4D] transition-all font-bold border-2 border-[#E85D4D]"
                  >
                    Admin Dashboard
                  </Link>
                )}
                <div className="px-4 py-2 text-[#F4E4C1] text-sm">
                  Logged in as: <span className="font-bold">{user.name}</span>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setSidebarOpen(false);
                  }}
                  className="retro-button w-full flex items-center justify-center space-x-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  handleLogin();
                  setSidebarOpen(false);
                }}
                className="retro-button w-full"
              >
                Login
              </button>
            )}
          </div>

          {/* Social Links */}
          {(socialLinks.facebook || socialLinks.instagram || socialLinks.youtube) && (
            <div className="mt-8 pt-6 border-t-2 border-[#6B4423]">
              <h3 className="text-lg font-bold text-[#F4E4C1] mb-4">Follow Us</h3>
              <div className="flex gap-4">
                {socialLinks.facebook && (
                  <a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#F4E4C1] hover:text-[#E8934D] transition-colors"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                )}
                {socialLinks.instagram && (
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#F4E4C1] hover:text-[#E8934D] transition-colors"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                )}
                {socialLinks.youtube && (
                  <a
                    href={socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#F4E4C1] hover:text-[#E8934D] transition-colors"
                  >
                    <Youtube className="w-6 h-6" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main>{children}</main>

      {/* Admin inline edit-mode toggle */}
      <EditModeToggle />

      {/* Floating Donation Button */}
      <DonationButton />
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />

      {/* Footer */}
      <footer className="bg-[#3D2817] border-t-4 border-[#6B4423] mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl retro-title mb-4">AcuErnie</h3>
              <p className="text-[#D4A574]">Doctoral Student & Senior Intern</p>
              <p className="text-[#D4A574] text-sm mt-2">Traditional Chinese Medicine & Acupuncture</p>
              {user?.role === 'admin' && (
                <Link to="/webmaster" className="text-[#D4A574] text-sm mt-2 block hover:text-[#E8934D] transition-colors">
                  Webmaster
                </Link>
              )}
            </div>
            <div>
              <h3 className="text-xl retro-title mb-4">Quick Links</h3>
              <div className="flex flex-col space-y-2">
                <Link to="/about" className="text-[#E8934D] hover:text-[#E8C04D]">About</Link>
                <Link to="/patient-videos" className="text-[#E8934D] hover:text-[#E8C04D]">Patient Videos</Link>
                <Link to="/internal-practices" className="text-[#E8934D] hover:text-[#E8C04D]">Internal Practices</Link>
              </div>
            </div>
            <div>
              <h3 className="text-xl retro-title mb-4">Newsletter</h3>
              <p className="text-[#D4A574] text-sm mb-3">Stay updated with our latest insights</p>
              <FooterMailingList />
            </div>
          </div>
          
          {/* Social Media Icons */}
          <div className="mt-8 pt-8 border-t-2 border-[#6B4423]">
            <div className="flex justify-center gap-6">
              {socialLinks.facebook ? (
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E8934D] hover:text-[#E8C04D] transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-8 h-8" />
                </a>
              ) : (
                <div className="text-[#6B4423] opacity-50">
                  <Facebook className="w-8 h-8" />
                </div>
              )}
              {socialLinks.instagram ? (
                <a
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E8934D] hover:text-[#E8C04D] transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-8 h-8" />
                </a>
              ) : (
                <div className="text-[#6B4423] opacity-50">
                  <Instagram className="w-8 h-8" />
                </div>
              )}
              {socialLinks.youtube ? (
                <a
                  href={socialLinks.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E8934D] hover:text-[#E8C04D] transition-colors"
                  aria-label="YouTube"
                >
                  <Youtube className="w-8 h-8" />
                </a>
              ) : (
                <div className="text-[#6B4423] opacity-50">
                  <Youtube className="w-8 h-8" />
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t-2 border-[#6B4423] text-center">
            <p className="text-[#D4A574] text-sm">&copy; {new Date().getFullYear()} AcuErnie. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}